import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import { type DiscountDocument } from "@fitness-app/data-models/entities/Discount";

import { RequestStatus } from "../../../enums/requestStatus";
import { addDiscount } from "./actions/addDiscount";
import { fetchDiscounts } from "./actions/fetchDiscounts";
import { updateDiscount } from "./actions/updateDiscount";
import { DISCOUNT_REDUCER_NAME, type DiscountReducer } from "./types";

const initialState: DiscountReducer = {
  discounts: [],
  discountsListStatus: null,
  discountAction: null,
  discountsError: null,
  addingNewDiscount: false,
};

const discountSlice = createSlice({
  name: DISCOUNT_REDUCER_NAME,
  initialState,
  reducers: {
    subscribeToDiscountsStarted(state) {
      state.discountsListStatus = RequestStatus.SUBSCRIBING;
      state.discountsError = null;
    },
    subscribeToDiscountsSuccess(state) {
      state.discountsListStatus = RequestStatus.SUBSCRIBED;
    },
    subscribeToDiscountsFailed(state, { payload }: PayloadAction<string>) {
      state.discountsListStatus = RequestStatus.FAILED;
      state.discountsError = payload;
    },
    unsubscribeFromDiscounts(state) {
      state.discountsListStatus = null;
      state.discounts = [];
      state.discountsError = null;
    },
    removeDiscountFromList(state, { payload }: PayloadAction<string>) {
      state.discounts = state.discounts.filter((discount) => discount.id !== payload);
    },
    addDiscountToList(state, { payload }: PayloadAction<DiscountDocument>) {
      state.discounts = [payload, ...state.discounts];
    },
    updateDiscountOnList(state, { payload }: PayloadAction<DiscountDocument>) {
      state.discounts = state.discounts.map((discount) => {
        if (discount.id === payload.id) {
          return {
            ...discount,
            ...payload,
          };
        }
        return discount;
      });
    },
  },
  extraReducers: (builder) => {
    builder.addCase(addDiscount.pending, (state) => {
      state.addingNewDiscount = true;
    });
    builder.addCase(addDiscount.fulfilled, (state) => {
      state.addingNewDiscount = false;
    });
    builder.addCase(addDiscount.rejected, (state) => {
      state.addingNewDiscount = false;
    });
    builder.addCase(updateDiscount.pending, (state, { meta }) => {
      state.discountAction = meta.arg.payload
        ? {
            id: meta.arg.payload.id,
            action: meta.arg.payload.action,
          }
        : null;
    });
    builder.addCase(updateDiscount.fulfilled, (state) => {
      state.discountAction = null;
    });
    builder.addCase(updateDiscount.rejected, (state) => {
      state.discountAction = null;
    });
    builder.addCase(fetchDiscounts.pending, (state) => {
      state.discountsListStatus = RequestStatus.FETCHING;
      state.discounts = [];
    });
    builder.addCase(fetchDiscounts.rejected, (state) => {
      state.discountsListStatus = RequestStatus.FAILED;
    });
    builder.addCase(fetchDiscounts.fulfilled, (state, { payload }) => {
      state.discountsListStatus = RequestStatus.SUBSCRIBED;
      state.discounts = payload;
    });
  },
});

export const {
  subscribeToDiscountsFailed,
  subscribeToDiscountsStarted,
  subscribeToDiscountsSuccess,
  unsubscribeFromDiscounts,
  removeDiscountFromList,
  addDiscountToList,
  updateDiscountOnList,
} = discountSlice.actions;

export default discountSlice.reducer;
